import { nextTick } from '../utils/nextTick.js';
import { ClassBase } from './ClassBase.js'
import {pubsub} from './pubsub.js'

/**
@class Anim
@classDesc アニメーション用のクラス
@param {JQuery | HTMLElement | String} el 対象のルート要素
@param {Anim#Options} [op] オプション

@example <caption>HTML</caption>
<div class="example"></div>
<button type="button" class="exampleBtn">example btn</button>

@example <caption>SCSS</caption>
.example {
	width: 100px;
	height: 100px;
	background: #000;
	transition: opacity 1s;
	display: none;
	opacity: 0;
	&-enter-active { display: block; }
	&-enter { opacity: 0; }
	&-enter-to { opacity: 1; }
	&-leave-active { display: block; }
	&-leave { opacity: 1; }
	&-leave-to { opacity: 0; }
}


@example <caption>JS:open or close or toggleメソッドが返すPromiseで何かするパターン</caption>
import {Anim} from '/assets/js/modules/Anim.js';

const example = new Anim('.example');

$(document).on('click', '.exampleBtn', async () => {
	console.log('before anim', example.isEnter)
	await example.toggle();
	console.log('after anim', example.isEnter)
})

@example <caption>JS:オプションにコールバック関数渡して何かするパターン</caption>
import {Anim} from '/assets/js/modules/Anim.js';

const example = new Anim('.example', {
	beforeEnter(instance){
		console.log('beforeEnter', instance);
	},
	afterEnter(instance){
		console.log('afterEnter', instance);
	},
	beforeLeave(instance){
		console.log('beforeLeave', instance);
	},
	afterLeave(instance){
		console.log('afterLeave', instance);
	},
});
$(document).on('click', '.exampleBtn', async () => {
	example.toggle();
})


@example <caption>JS:イベントの発火を拾って何かするパターン</caption>
import {Anim} from '/assets/js/modules/Anim.js';

const example = new Anim('.example');

$(document).on('click', '.exampleBtn', async () => {
	example.toggle();
})
$('.example').on('anim:beforeEnter', (e, instance) =>  {
	e.preventDefault();
	console.log('anim:beforeEnter', instance);
});
$('.example').on('anim:afterEnter', (e, instance) =>  {
	e.preventDefault();
	console.log('anim:afterEnter', instance);
});
$('.example').on('anim:beforeLeave', (e, instance) =>  {
	e.preventDefault();
	console.log('anim:beforeLeave', instance);
});
$('.example').on('anim:afterLeave', (e, instance) =>  {
	e.preventDefault();
	console.log('anim:afterLeave', instance);
});
 */

/**
@typedef {Object} Anim#Options Animクラスのオプション
@prop {String} [animClass=elに付いてるクラスの１番目]  アニメーション用のクラス名
@prop {Anim#beforeEnter} [beforeEnter] アニメーションEnter前に実行される関数
@prop {Anim#afterEnter} [afterEnter] アニメーションEnter後に実行される関数
@prop {Anim#beforeLeave} [beforeLeave] アニメーションLeave前に実行される関数
@prop {Anim#afterLeave} [afterLeave] アニメーションLeave後に実行される関数
*/

/**
	アニメーションEnter前に実行される関数
	@callback Anim#beforeEnter
	@param {Anim} instance {@link Anim}のインスタンス
*/

/**
	アニメーションEnter後に実行される関数
	@callback Anim#afterEnter
	@param {Anim} instance {@link Anim}のインスタンス
*/

/**
	アニメーションLeave前に実行される関数
	@callback Anim#beforeLeave
	@param {Anim} instance {@link Anim}のインスタンス
*/

/**
	アニメーションLeave後に実行される関数
	@callback Anim#afterLeave
	@param {Anim} instance {@link Anim}のインスタンス
*/

/**
	アニメーションEnter前に発火するイベント
	@event Anim#anim:beforeEnter
	@param {Anim} instance {@link Anim}のインスタンス
*/

/**
	アニメーションEnter後に発火するイベント
	@event Anim#anim:afterEnter
	@param {Anim} instance {@link Anim}のインスタンス
*/

/**
	アニメーションLeave前に発火するイベント
	@event Anim#anim:beforeLeave
	@param {Anim} instance {@link Anim}のインスタンス
*/

/**
	アニメーションLeave後に発火するイベント
	@event Anim#anim:afterLeave
	@param {Anim} instance {@link Anim}のインスタンス
*/



export class Anim extends ClassBase {
	#classList = [
		'-enter-active',
		'-enter',
		'-enter-to',
		'-leave-active',
		'-leave',
		'-leave-to',
	]

	constructor(el, op) {
		super(el);
		this.op = {
			...this.op,
			baseClass: this.$el.attr('class').split(' ').filter(i => i)[0],
			beforeEnter: null,
			afterEnter: null,
			beforeLeave: null,
			afterLeave: null,
			...op,
		}
		delete this.op.addEvent;

		/**
		 * アニメーションがenter状態か否か
		 * @type {Boolean}
		 */
		this.isEnter = false;

		this.#classList = this.#classList.map(el => `${this.op.baseClass}${el}`);
	}


	/**
	 * アニメーションEnter
	 * @returns {Promise<Anim>}
	 */
	enter(){
		return new Promise(resolve => {
			this.$el.trigger('anim:beforeEnter', this)
			pubsub.pub('anim:beforeEnter', this)
			this.op.beforeEnter && this.op.beforeEnter(this)

			this.isEnter = true
			this.$el
				.removeClass(this.#classList.join(' '))
				.off('transitionend')
				.on('transitionend', (e)=>{
					if(e.originalEvent.target != this.$el[0]) return;
					this.$el.off('transitionend')
					this.$el.trigger('anim:afterEnter', this)
					pubsub.pub('anim:afterEnter', this)
					this.op.afterEnter && this.op.afterEnter(this)
					resolve(this);
				})
				.addClass(this.#classList.slice(0, 2).join(' '));
			nextTick(() => {
				this.$el.addClass(this.#classList[2])
			})
		})
	}


	/**
	 * アニメーションLeave
	 * @returns {Promise<Anim>}
	 */
	leave(){
		return new Promise(resolve => {
			this.$el.trigger('anim:beforeLeave', this)
			pubsub.pub('anim:beforeLeave', this)
			this.op.beforeLeave && this.op.beforeLeave(this)

			this.isEnter = false
			this.$el
				.removeClass(this.#classList.slice(0, 3).join(' '))
				.off('transitionend')
				.on('transitionend', (e)=>{
					if(e.originalEvent.target != this.$el[0]) return;
					this.$el.off('transitionend').removeClass(this.#classList.join(' '))
					this.$el.trigger('anim:afterLeave', this)
					pubsub.pub('anim:afterLeave', this)
					this.op.afterLeave && this.op.afterLeave(this)
					resolve(this);
				})
				.addClass(this.#classList.slice(3, 5).join(' '));
			nextTick(() => {
				this.$el.addClass(this.#classList[5])
			})
		})
	}

	/**
	 * アニメーションToggle
	 * @returns {Promise<Anim>}
	 */
	toggle(){
		return this.isEnter ? this.leave() : this.enter()
	}
}